import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import theme from '@/styles/theme';
import { BrowserRouter } from 'react-router-dom';
import Modal from '../common-modal';
import Button from '../button';

const Container = styled.div<{maxHeight?:number}>`
  user-select: none;
  overflow-y: auto;
  outline: none;
  width: 100%;
  height: 100%;
  max-width: 394px;
  max-height: ${({ maxHeight }) => maxHeight || 240}px;
  box-sizing: border-box;
  padding: 0px 10px;
`;

const Check = styled.div<{maxHeight?:number}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 10px;
  user-select: none;
  overflow-y: auto;
  outline: none;
  width: 100%;
  height: 100%;
  max-width: 394px;
  max-height: ${({ maxHeight }) => maxHeight || 240}px;
  border-radius: 20px;
  align-items: center;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 28px;
`;

const Title = styled.div`
  /* font-size: 24px;
  color: ${theme.color.gray};
  font-family: ${theme.fontFamily.bold}; */

  color: ${theme.color.black};
  font-family: ${theme.fontFamily.semiBold};
  font-size: 18px;
  line-height: 26px;
  white-space:pre-wrap;
  text-align: center;
`;

const ConfirmDesc = styled.div`
  padding-top: 12px;
  color: ${theme.color.black};
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 20px;
  white-space:pre-wrap;
  text-align: center;
  &.second {
    padding-top: 3px;
  }
`;

const Desc = styled.div`
  /* font-size: 14px;
  font-family: ${theme.fontFamily.regular};
  margin-top: 8px; */

  padding-top: 30px;
  text-align: center;
  /* max-width: 280px; */
  padding-left: 30px;
  padding-right: 30px;
  /* max-width: 216px; */

  font-family: ${theme.fontFamily.semiBold};
  font-size: 18px;
  line-height: 26px;

  white-space:pre-wrap;
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  color: ${theme.color.disabled};
  font-size: 14px;
  font-family: ${theme.fontFamily.regular};
  margin-top: 14px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;

  &:before {
    content: "·";
    display: inline-block;
    margin-right: 4px;
  }
`;

const ButtonsWrap = styled.div`
  display: flex;
  align-items: center;
  /* margin-top: 24px; */
  /* padding-top: 50px; */
  /* justify-content: flex-end; */
  width: 100%;
  justify-content: center;
  box-sizing: border-box;
  padding: 0px 10px;
  
  > button {
    height: 36px;
    border-radius: 8px;

    &.disable {
      display: none;
    }
  }

  > button + button {
    margin-left: 12px;
  }
`;

const Divider = styled.div`
width: 100%;

  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  em {
    color: #5754e0;
    font-style: normal;
  }
`;

interface ButtonProps {
  type: 'confirm' | 'cancel' | 'logout' |'confirmWithMessage'|'alertWithMessage' | 'preparingWithMessage';
  label: string;
  value?: string;
  color?: string;
  disabled?: boolean;
  className?: string;
  width?: string;
}

interface IAlertProps {
  title?: string;
  desc?: string;
  list?: string[];
  buttons?: ButtonProps[];
  customJSX?: JSX.Element;
  onConfirm?: (result: boolean | string) => void;
  allowKeyPress?: boolean;
  maxHeight?:number;
}

const ROOT_ID = 'common-alert-root';

const Alert: React.FC<IAlertProps> = ({
  title = '',
  desc = '',
  list = [],
  buttons = [],
  customJSX = <></>,
  onConfirm,
  allowKeyPress = false,
  maxHeight,
}) => {
  const [show, setShow] = useState(true);
  const modalRef = useRef<HTMLDivElement>(null);

  const onConfirmClick = useCallback((value) => async () => {
    if (onConfirm) {
      await onConfirm(value || true);
    }
    setShow(false);
  }, [onConfirm]);

  const onCancelClick = useCallback(async () => {
    if (onConfirm) {
      await onConfirm(false);
    }
    setShow(false);
  }, [onConfirm]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, [modalRef]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!allowKeyPress) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (e.key === 'Escape') {
      onCancelClick();
    } else if (e.key === 'Enter') {
      onConfirmClick(undefined);
    }
  }, [allowKeyPress, onCancelClick, onConfirmClick]);

  return (
    <BrowserRouter>
      <Modal show={show} closeOnClickOverlay={false} zIndex={9999}>
        <Container ref={modalRef} onKeyDown={onKeyDown} tabIndex={-1} maxHeight={maxHeight}>
          <Check maxHeight={maxHeight}>
            <Divider>
              {title && <Title>{title}</Title>}
              {desc && (
                (buttons[1]?.type === 'confirmWithMessage'
            || buttons[0]?.type === 'alertWithMessage')
                  ? (
                    <ConfirmDesc>
                      {desc}
                    </ConfirmDesc>
                  )
                  : (
                    <Desc>
                      {desc}
                    </Desc>
                  ))}
              {list.length > 0 && (
                <ListWrap>
                  {list.map((li) => (
                    <ListItem key={li}>{li}</ListItem>
                  ))}
                </ListWrap>
              )}
              {customJSX}
            </Divider>
            {buttons.length > 0 && (
              <ButtonsWrap>
                {buttons.map(({
                  type, label, color, value, width = '', disabled, className,
                }) => (
                  <React.Fragment key={`${type}-${value}`}>
                    {type === 'confirm' || type === 'confirmWithMessage' ? (
                      <Button
                        size="large"
                        fillColor={disabled ? theme.color.disabled : color}
                        className={className}
                        // data-value={value}
                        onClick={disabled ? undefined : onConfirmClick(value)}
                        buttonWidth={width}
                      >
                        {label}
                      </Button>
                    ) : (
                      <Button
                        size="large"
                        fillColor={color || theme.color.disabled}
                        className={className}
                        onClick={disabled ? undefined : onCancelClick}
                        buttonWidth={width}
                      >
                        {label}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </ButtonsWrap>
            )}
          </Check>
        </Container>
      </Modal>
    </BrowserRouter>
  );
};

const onConfirm = (
  resolve: (value: boolean | string | PromiseLike<boolean | string>) => void,
) => (
  result: boolean | string,
) => {
  resolve(result);
  const alertRootDiv = document.getElementById(ROOT_ID);
  if (alertRootDiv) {
    ReactDOM.unmountComponentAtNode(alertRootDiv as Element);
  }
};

const showAlert = (props: IAlertProps) => new Promise<boolean | string>((resolve) => {
  let alertRootDiv = document.getElementById(ROOT_ID);
  if (!alertRootDiv) {
    alertRootDiv = document.createElement('div');
    alertRootDiv.id = ROOT_ID;
    document.body.append(alertRootDiv);
  }

  ReactDOM.render(
    <Alert {...props} onConfirm={onConfirm(resolve)} />,
    alertRootDiv,
  );
});

export const hideAlert = () => {
  const alertRootDiv = document.getElementById(ROOT_ID);
  if (alertRootDiv) {
    ReactDOM.unmountComponentAtNode(alertRootDiv);
  }
};

export default showAlert;
